import {Injectable} from '@angular/core';

/**
 * Shared "does the user currently have unsaved form edits" registry
 * (BRIEFD-7315).
 *
 * Root cause: several completely unrelated triggers — the top-nav
 * business-unit switcher, the browser back button, and the AI
 * description "regenerate" button — all discard in-progress form edits
 * with no warning, because each of them lives far from the form
 * component's own dirty state and has no way to ask "is anything unsaved
 * right now?" before acting.
 *
 * Any component that owns a form a user could lose work from registers a
 * cheap dirty-check callback here on init and unregisters it on destroy.
 * Call sites that are about to do something destructive (switch business
 * unit, navigate away, overwrite a field) call `hasUnsavedChanges()` first
 * and warn if it comes back true — instead of each trigger reinventing its
 * own narrow "is the vehicle form dirty" check.
 */
@Injectable({
  providedIn: 'root',
})
export class UnsavedChangesService {
  private readonly checks = new Set<() => boolean>();

  /** Register a dirty-check callback. Returns an unregister function —
   *  call it from the owning component's `ngOnDestroy`. */
  register(check: () => boolean): () => void {
    this.checks.add(check);
    return () => this.checks.delete(check);
  }

  /** True if ANY currently-registered form reports unsaved changes. */
  hasUnsavedChanges(): boolean {
    for (const check of this.checks) {
      try {
        if (check()) {
          return true;
        }
      } catch {
        // A stale/misbehaving check must never block navigation entirely.
      }
    }
    return false;
  }
}
